import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Container,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { pageColors } from './HomePage';
import apiFetch from '../api/apiFetch';

const UI_VARIANT_KEY = 'uiVariant';
const MIN_MONTH = dayjs('2024-01-01');
const MAX_MONTH = dayjs('2028-12-31');

const clampMonth = (value) => {
  if (!value || !value.isValid()) return dayjs().startOf('month');
  if (value.isBefore(MIN_MONTH)) return MIN_MONTH;
  if (value.isAfter(MAX_MONTH)) return MAX_MONTH;
  return value.startOf('month');
};

const columns = [
  { id: 'itemDescription', label: 'Article Description', type: 'string', width: '22%' },
  { id: 'supplierName', label: 'Supplier', type: 'string', width: '16%' },
  { id: 'givenNumber', label: 'Invoice Number', type: 'string', width: '14%' },
  { id: 'quantity', label: 'Quantity', type: 'number', width: '12%', align: 'right' },
  { id: 'unit', label: 'Unit', type: 'string', width: '10%' },
  { id: 'total', label: 'Row Total', type: 'number', width: '13%' , align: 'right' },
  { id: 'invoiceTotal', label: 'Invoice Total', type: 'number', width: '13%', align: 'right' },
];

export default function SupplierInvoicesPage({ connected, setConnected, setError, loadingConnection }) {
  const [selectedMonth, setSelectedMonth] = useState(clampMonth(dayjs()));
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState('itemDescription');
  const [order, setOrder] = useState('asc');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [invoiceFilter, setInvoiceFilter] = useState('');
  const [articleFilter, setArticleFilter] = useState('');
  const [uiVariant, setUiVariant] = useState(() => localStorage.getItem(UI_VARIANT_KEY) || 'classic');

  const isModern = uiVariant === 'modern';

  useEffect(() => {
    const handleVariantChange = (event) => {
      const nextVariant = event?.detail || localStorage.getItem(UI_VARIANT_KEY) || 'classic';
      setUiVariant(nextVariant);
    };

    const handleStorage = (event) => {
      if (event.key === UI_VARIANT_KEY) {
        setUiVariant(event.newValue || 'classic');
      }
    };

    window.addEventListener('ui-variant-changed', handleVariantChange);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('ui-variant-changed', handleVariantChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const supplierInvoicesTheme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: pageColors.supplierInvoices,
          },
        },
      }),
    []
  );

  const fetchSupplierInvoiceRows = async () => {
    if (!connected) {
      setError('Not connected to Fortnox. Please connect first.');
      setConnected(false);
      setRows([]);
      setMeta(null);
      return;
    }

    setLoading(true);
    try {
      const monthParam = clampMonth(selectedMonth).format('YYYY-MM');
      const response = await apiFetch(`/api/supplier-invoices/rows?month=${monthParam}`);

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg;
        if (response.status === 401) {
          errMsg = 'Lost connection to Fortnox: Unauthorized. Please reconnect.';
          setConnected(false);
        } else if (response.status === 429) {
          errMsg = 'Fortnox cannot handle requests at the moment. Please wait.';
          setConnected(true);
        } else {
          errMsg = `Failed to fetch supplier invoice rows: ${errorText || response.status}.`;
          setConnected(false);
        }
        setError(errMsg);
        setRows([]);
        setMeta(null);
        return;
      }

      const payload = await response.json();
      const nextRows = Array.isArray(payload?.rows) ? payload.rows : [];
      setRows(nextRows);
      setMeta(payload?.meta || null);
      setConnected(true);
    } catch (error) {
      setError(error.message);
      setConnected(false);
      setRows([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loadingConnection) return;
    fetchSupplierInvoiceRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, connected, loadingConnection]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredRows = useMemo(() => {
    const supplierNeedle = supplierFilter.trim().toLowerCase();
    const invoiceNeedle = invoiceFilter.trim().toLowerCase();
    const articleNeedle = articleFilter.trim().toLowerCase();

    return rows.filter((row) => {
      const supplierMatch =
        !supplierNeedle ||
        [row.supplierName, row.supplierNumber].some((value) => (value || '').toLowerCase().includes(supplierNeedle));
      const invoiceMatch =
        !invoiceNeedle ||
        [row.invoiceNumber, row.externalInvoiceNumber, row.givenNumber].some((value) =>
          (value || '').toLowerCase().includes(invoiceNeedle)
        );
      const articleMatch =
        !articleNeedle ||
        [row.articleNumber, row.itemId, row.itemDescription].some((value) =>
          (value || '').toLowerCase().includes(articleNeedle)
        );

      return supplierMatch && invoiceMatch && articleMatch;
    });
  }, [rows, supplierFilter, invoiceFilter, articleFilter]);

  const sortedRows = useMemo(() => {
    const column = columns.find((item) => item.id === orderBy);
    const type = column?.type || 'string';
    const compare = (a, b) => {
      const valA = a[orderBy];
      const valB = b[orderBy];

      if (type === 'date') {
        const dateA = dayjs(valA);
        const dateB = dayjs(valB);
        if (!dateA.isValid() && !dateB.isValid()) return 0;
        if (!dateA.isValid()) return 1;
        if (!dateB.isValid()) return -1;
        return order === 'asc' ? dateA.valueOf() - dateB.valueOf() : dateB.valueOf() - dateA.valueOf();
      }

      if (type === 'number') {
        const numA = Number(valA);
        const numB = Number(valB);
        const aValid = Number.isFinite(numA);
        const bValid = Number.isFinite(numB);
        if (!aValid && !bValid) return 0;
        if (!aValid) return 1;
        if (!bValid) return -1;
        return order === 'asc' ? numA - numB : numB - numA;
      }

      const strA = (valA || '').toString().toLowerCase();
      const strB = (valB || '').toString().toLowerCase();
      if (strA < strB) return order === 'asc' ? -1 : 1;
      if (strA > strB) return order === 'asc' ? 1 : -1;
      return 0;
    };

    return [...filteredRows].sort(compare);
  }, [filteredRows, order, orderBy]);

  const uniqueSuppliers = useMemo(() => {
    return new Set(rows.map((row) => row.supplierNumber || row.supplierName).filter(Boolean)).size;
  }, [rows]);

  const totalInvoiceAmount = useMemo(() => {
    return filteredRows.reduce((sum, row) => sum + (Number(row.total) || 0), 0);
  }, [filteredRows]);

  const formatDate = (value) => {
    if (!value) return '-';
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : value;
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return value;
    return Number.isInteger(numeric) ? numeric.toLocaleString() : numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getDisplayValue = (row, column) => {
    if (column.type === 'date') return formatDate(row[column.id]);
    if (column.type === 'number') return formatNumber(row[column.id]);
    return row[column.id] || '-';
  };

  const handleExportCsv = () => {
    if (!sortedRows.length) {
      setError('No supplier invoice rows to export.');
      return;
    }

    const escapeValue = (value) => {
      const text = value == null ? '' : String(value);
      if (text.includes(',') || text.includes('"') || text.includes('\n')) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };

    const header = columns.map((column) => column.label);
    const lines = [
      header.map(escapeValue).join(','),
      ...sortedRows.map((row) =>
        columns
          .map((column) => {
            const rawValue = column.type === 'date' ? formatDate(row[column.id]) : row[column.id];
            return escapeValue(rawValue ?? '');
          })
          .join(',')
      ),
    ];

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `supplier_invoice_rows_${selectedMonth.format('YYYY-MM')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const renderTruncatedNotice = () => {
    if (!meta?.truncated) return null;
    const message =
      meta.message ||
      `Displaying ${meta.returnedCount ?? '?'} rows out of ${meta.fetchedCount ?? '?'} fetched rows. Results are truncated at ${meta.maxRecords ?? '?'}.`;
    return <Alert severity="info">{message}</Alert>;
  };

  const renderFilterBar = (variant) => (
    <Paper
      elevation={variant === 'modern' ? 0 : 3}
      sx={{
        p: variant === 'modern' ? 2.5 : 3,
        borderRadius: variant === 'modern' ? 3 : 2,
        border: variant === 'modern' ? '1px solid rgba(0,0,0,0.05)' : undefined,
        boxShadow: variant === 'modern' ? '0 12px 25px rgba(0,0,0,0.04)' : undefined,
        backgroundColor: '#fff',
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {variant === 'modern' && (
            <Avatar sx={{ width: 42, height: 42, bgcolor: `${pageColors.supplierInvoices}18`, color: pageColors.supplierInvoices }}>
              <FilterAltIcon fontSize="small" />
            </Avatar>
          )}
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
        </Stack>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ alignItems: { xs: 'stretch', md: 'center' } }}
        >
          <Box sx={{ width: { xs: '100%', md: 180 }, flexShrink: 0 }}>
            <DesktopDatePicker
              label="Month"
              inputFormat="YYYY-MM"
              views={['year', 'month']}
              value={selectedMonth}
              onChange={(newValue) => {
                if (newValue) setSelectedMonth(clampMonth(newValue));
              }}
              minDate={MIN_MONTH}
              maxDate={MAX_MONTH}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 40,
                    },
                  }}
                />
              )}
            />
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 0' }, minWidth: 0 }}>
            <TextField
              label="Supplier"
              size="small"
              fullWidth
              value={supplierFilter}
              onChange={(event) => setSupplierFilter(event.target.value)}
              sx={{
                '& .MuiInputBase-root': {
                  height: 40,
                },
              }}
            />
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 0' }, minWidth: 0 }}>
            <TextField
              label="Invoice number"
              size="small"
              fullWidth
              value={invoiceFilter}
              onChange={(event) => setInvoiceFilter(event.target.value)}
              sx={{
                '& .MuiInputBase-root': {
                  height: 40,
                },
              }}
            />
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 0' }, minWidth: 0 }}>
            <TextField
              label="Article description"
              size="small"
              fullWidth
              value={articleFilter}
              onChange={(event) => setArticleFilter(event.target.value)}
              sx={{
                '& .MuiInputBase-root': {
                  height: 40,
                },
              }}
            />
          </Box>
          <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={fetchSupplierInvoiceRows}
              disabled={loading}
              sx={{ width: 160, minWidth: 160, height: 40 }}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCsv}
              disabled={!sortedRows.length || loading}
              sx={{ width: 160, minWidth: 160, height: 40 }}
            >
              Export CSV
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );

  const renderTable = (variant) => (
    <TableContainer
      component={Paper}
      elevation={variant === 'modern' ? 0 : 3}
      sx={{
        borderRadius: variant === 'modern' ? 3 : 2,
        border: variant === 'modern' ? '1px solid rgba(0,0,0,0.05)' : undefined,
        boxShadow: variant === 'modern' ? '0 20px 40px rgba(0,0,0,0.06)' : undefined,
        backgroundColor: '#fff',
        overflowX: 'auto',
      }}
    >
      <Table stickyHeader sx={{ width: '100%', tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align || 'left'}
                sx={{
                  width: column.width,
                  backgroundColor: variant === 'modern' ? '#fafafa' : undefined,
                  fontWeight: 700,
                }}
              >
                <TableSortLabel
                  active={orderBy === column.id}
                  direction={orderBy === column.id ? order : 'asc'}
                  onClick={() => handleRequestSort(column.id)}
                >
                  {column.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 5 }}>
                No supplier invoice rows match the current filters.
              </TableCell>
            </TableRow>
          ) : (
            sortedRows.map((row, index) => (
              <TableRow key={`${row.invoiceNumber || 'invoice'}-${row.itemId || row.articleNumber || index}`}>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    sx={{
                      whiteSpace: column.id === 'itemDescription' ? 'normal' : 'nowrap',
                      wordBreak: column.id === 'itemDescription' ? 'break-word' : 'normal',
                    }}
                  >
                    {getDisplayValue(row, column)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderClassicLayout = () => (
    <Box
      sx={{
        backgroundColor: `${pageColors.supplierInvoices}15`,
        minHeight: '100vh',
        pt: { xs: 3, md: 4 },
        pb: { xs: 6, md: 8 },
        px: { xs: 1.5, sm: 3 },
      }}
    >
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ color: pageColors.supplierInvoices, fontWeight: 'bold', mb: 3 }}>
          Supplier Invoice Rows
        </Typography>
        <Stack spacing={3}>
          {renderFilterBar('classic')}
          {loading && <LinearProgress />}
          {renderTruncatedNotice()}
          {renderTable('classic')}
        </Stack>
      </Container>
    </Box>
  );

  const renderModernLayout = () => (
    <Box
      sx={{
        background: 'radial-gradient(circle at top left, rgba(69,90,100,0.16), transparent 55%), #f7f9fb',
        minHeight: '100vh',
        pt: 3,
        pb: 6,
      }}
    >
      <Container maxWidth="xl">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 4,
            backgroundColor: '#fff',
            boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
            mb: 3,
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: pageColors.supplierInvoices, color: '#fff' }}>
              <RequestQuoteIcon />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" sx={{ letterSpacing: 3, color: 'text.secondary' }}>
                FIORDILATTE FOOD
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                Supplier Invoice Rows
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Review monthly supplier invoice lines, filter them down, and export the current view to CSV.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        

        <Stack spacing={3}>
          {renderFilterBar('modern')}
          {(loadingConnection || loading) && <LinearProgress />}
          {renderTruncatedNotice()}
          {renderTable('modern')}
        </Stack>
      </Container>
    </Box>
  );

  return (
    <ThemeProvider theme={supplierInvoicesTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {isModern ? renderModernLayout() : renderClassicLayout()}
      </LocalizationProvider>
    </ThemeProvider>
  );
}
